import { z } from 'zod';

/**
 * Visibility of a shareable resource (case, conversation…) — resource-sharing.
 *
 * - `private` : accessible only to its owner + users explicitly shared with.
 * - `cabinet` : accessible to every member of the owning cabinet. New cases and
 *   template packs intentionally default to this value so cabinet work remains
 *   collaborative unless the owner explicitly switches the resource to private.
 *   Conversations still default to private because chat history has a narrower
 *   authorship model.
 *
 * WHY a shared enum: both the write path (`POST /cases` validation) and the
 * share/visibility endpoints (T7) must agree on the exact accepted values; a
 * stray string would either 400 legitimate requests or let an invalid value
 * reach the `cases.visibility` / `casediscovery_conversations.visibility`
 * CHECK constraint.
 */
export const VisibilitySchema = z.enum(['private', 'cabinet']);

/** Visibility of a shareable resource. */
export type Visibility = z.infer<typeof VisibilitySchema>;

/**
 * Role a *share row* can carry (`case_shares.role` / `conversation_shares.role`
 * CHECK constraint). An OWNER is NEVER a share role — ownership lives in
 * `cases.owner_id` / `casediscovery_conversations.user_id`, not in a share row.
 * This is the value the client sends when granting access.
 */
export const ShareRoleSchema = z.enum(['viewer', 'editor']);

/** Role carried by a share row (never `owner`). */
export type ShareRole = z.infer<typeof ShareRoleSchema>;

/**
 * Role RESOLVED for a user on a resource (`my_role`), computed by the unified
 * access guard (`services/access`) from ownership ∪ share ∪ visibility. Distinct
 * from {@link ShareRoleSchema}: it additionally carries `owner`, which is never a
 * persisted share value. Do NOT confuse the two — a share is written with a
 * `ShareRole`, but a caller reads back an `AccessRole`.
 */
export const AccessRoleSchema = z.enum(['owner', 'editor', 'viewer']);

/** Resolved access role (`owner` | `editor` | `viewer`) — the computed `my_role`. */
export type AccessRole = z.infer<typeof AccessRoleSchema>;

/**
 * A single share row on a case, as exposed to the owner UI.
 *
 * `granted_by` (who created the share) is intentionally exposed: these endpoints
 * are owner-only, so there is no cross-tenant leak — the owner is entitled to
 * audit who granted each share.
 */
export const CaseShareSchema = z.object({
  user_id: z.string().uuid(),
  role: ShareRoleSchema,
  granted_by: z.string().uuid(),
  created_at: z.string(),
});

/** A share row on a case (owner-facing DTO). */
export type CaseShare = z.infer<typeof CaseShareSchema>;

/** A single share row on a conversation, as exposed to the owner UI. */
export const ConversationShareSchema = z.object({
  user_id: z.string().uuid(),
  role: ShareRoleSchema,
  granted_by: z.string().uuid(),
  created_at: z.string(),
});

/** A share row on a conversation (owner-facing DTO). */
export type ConversationShare = z.infer<typeof ConversationShareSchema>;

/**
 * A single share row on a template pack, as exposed to the owner UI
 * (resource-sharing Phase 5). Identical shape to {@link CaseShareSchema} /
 * {@link ConversationShareSchema} — kept as a distinct type for domain clarity
 * (the three share tables `case_shares` / `conversation_shares` /
 * `template_shares` are independent) and so a caller can be explicit about which
 * resource it is sharing.
 */
export const TemplateShareSchema = z.object({
  user_id: z.string().uuid(),
  role: ShareRoleSchema,
  granted_by: z.string().uuid(),
  created_at: z.string(),
});

/** A share row on a template pack (owner-facing DTO). */
export type TemplateShare = z.infer<typeof TemplateShareSchema>;

/**
 * Body of `POST /cases/:caseId/shares` and
 * `POST /conversations/:conversationId/shares`. The recipient is identified by
 * `targetUserId`; the caller (owner) is taken from `req.user`, never the body.
 */
export const CreateShareRequestSchema = z.object({
  targetUserId: z.string().uuid(),
  role: ShareRoleSchema,
});

/** Parsed body of a create-share request. */
export type CreateShareRequest = z.infer<typeof CreateShareRequestSchema>;

/**
 * Body of `PATCH /cases/:caseId/visibility` and
 * `PATCH /conversations/:conversationId/visibility`. This is the ONLY authorized
 * path to mutate `visibility` (the generic case PATCH strips it via allowlist).
 */
export const UpdateVisibilityRequestSchema = z.object({
  visibility: VisibilitySchema,
});

/** Parsed body of an update-visibility request. */
export type UpdateVisibilityRequest = z.infer<typeof UpdateVisibilityRequestSchema>;

/**
 * Body of `PATCH /cabinets/:cabinetId/default-case-visibility` — réglage cabinet
 * « Partage des dossiers par défaut » (`cabinets.default_case_visibility`,
 * migration 192). `cabinet` = un nouveau dossier est partagé d'office à tous les
 * membres ; `private` = il n'est visible que de son créateur.
 *
 * `.strict()` : l'endpoint ne pilote QUE ce réglage ; une clé en trop (par ex.
 * `name`, `stripe_customer_id`) est une erreur d'appelant, pas un champ à ignorer.
 */
export const UpdateDefaultCaseVisibilityRequestSchema = z
  .object({ default_case_visibility: VisibilitySchema })
  .strict();

/** Parsed body of an update-default-case-visibility request. */
export type UpdateDefaultCaseVisibilityRequest = z.infer<
  typeof UpdateDefaultCaseVisibilityRequestSchema
>;

/**
 * Visibilité appliquée à un nouveau dossier quand son créateur n'en choisit pas.
 *
 * Toute valeur absente ou inconnue retombe sur `cabinet` : c'est le comportement
 * historique (défaut serveur et DEFAULT de `cases.visibility` depuis M135), donc
 * un cabinet qui n'a jamais touché au réglage — ou une base où la migration 192
 * n'est pas encore appliquée, la colonne étant alors absente de la ligne lue —
 * continue de créer des dossiers partagés à tout le cabinet.
 *
 * @param raw - `cabinets.default_case_visibility` telle que lue (peut manquer).
 */
export function resolveDefaultCaseVisibility(raw: unknown): Visibility {
  const parsed = VisibilitySchema.safeParse(raw);
  return parsed.success ? parsed.data : 'cabinet';
}
